/**
 * Agregador puro da serie diaria em dia / semana / mes.
 *
 * Decisoes nao obvias:
 *  1. Semana e ISO-8601: comeca na SEGUNDA e pertence ao ano da sua quinta-
 *     feira. E o que faz "semana de 29/12 a 04/01" ser uma barra so, em vez de
 *     duas metades em anos diferentes.
 *  2. Toda conta de data e em UTC sobre `YYYY-MM-DD`: o dia ja veio fechado no
 *     fuso do lojista pelo backend, e `new Date('2026-09-24')` em horario de
 *     verao poderia cair no dia anterior.
 *  3. Dinheiro e somado em centavos inteiros; nada e dividido aqui.
 *
 * Testes mentais (todos conferidos no calendario):
 *  - 2026-09-24 (quinta) -> chave '2026-W39', rotulo '21/09–27/09'.
 *  - 2026-09-20 (domingo) -> '2026-W38' (segunda 14/09), nao a semana seguinte.
 *  - 2026-01-01 (quinta) -> '2026-W01', rotulo '29/12–04/01'.
 *  - 2027-01-01 (sexta) -> '2026-W53': 2026 comeca em quinta e tem 53 semanas.
 *  - mes: '2026-09-05' -> '2026-09', rotulo 'set/26'.
 *  - 7 pontos de 21/09 a 27/09 em 'semana' -> 1 balde com a soma dos 7.
 */

import type { DailyPoint } from '../../../types';

export type Granularidade = 'dia' | 'semana' | 'mes';

export interface Balde {
  /** Chave ordenavel: 'YYYY-MM-DD', 'YYYY-Www' ou 'YYYY-MM'. */
  readonly chave: string;
  /** Rotulo curto do eixo X. */
  readonly rotulo: string;
  readonly abandonadoCents: number;
  readonly recuperadoCents: number;
  readonly abandonadoCount: number;
  readonly recuperadoCount: number;
}

export interface SerieAgregada {
  readonly granularidade: Granularidade;
  readonly baldes: readonly Balde[];
  readonly labels: readonly string[];
  readonly abandonadoCents: readonly number[];
  readonly recuperadoCents: readonly number[];
}

const DIA_MS = 86_400_000;
const MESES_CURTOS: readonly string[] = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

export const GRANULARIDADE_LABEL: Readonly<Record<Granularidade, string>> = {
  dia: 'Dia',
  semana: 'Semana',
  mes: 'Mês',
};

/** `YYYY-MM-DD` -> meia-noite UTC. Entrada invalida vira a epoca, nunca NaN. */
function dataUtc(day: string): Date {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1));
}

function somarDias(date: Date, dias: number): Date {
  return new Date(date.getTime() + dias * DIA_MS);
}

/** 0 = segunda ... 6 = domingo. */
function diaDaSemanaIso(date: Date): number {
  return (date.getUTCDay() + 6) % 7;
}

function segundaDaSemana(date: Date): Date {
  return somarDias(date, -diaDaSemanaIso(date));
}

function semanaIso(date: Date): { readonly ano: number; readonly semana: number } {
  const quinta = somarDias(date, 3 - diaDaSemanaIso(date));
  const ano = quinta.getUTCFullYear();
  // A semana 1 e a que contem 4 de janeiro; sua segunda-feira e a origem.
  const quatroDeJaneiro = new Date(Date.UTC(ano, 0, 4));
  const segundaDaSemana1 = segundaDaSemana(quatroDeJaneiro);
  const semana = 1 + Math.floor((quinta.getTime() - segundaDaSemana1.getTime()) / (7 * DIA_MS));
  return { ano, semana };
}

function segundaDaChaveSemanal(chave: string): Date {
  const [anoTexto, semanaTexto] = chave.split('-W');
  const ano = Number(anoTexto ?? 1970);
  const semana = Number(semanaTexto ?? 1);
  const segundaDaSemana1 = segundaDaSemana(new Date(Date.UTC(ano, 0, 4)));
  return somarDias(segundaDaSemana1, (semana - 1) * 7);
}

function ddmm(date: Date): string {
  const d = String(date.getUTCDate()).padStart(2, '0');
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${d}/${m}`;
}

export function chaveDoBalde(day: string, granularidade: Granularidade): string {
  switch (granularidade) {
    case 'dia':
      return day.slice(0, 10);
    case 'semana': {
      const { ano, semana } = semanaIso(dataUtc(day));
      return `${ano}-W${String(semana).padStart(2, '0')}`;
    }
    case 'mes':
      return day.slice(0, 7);
  }
}

export function rotuloDoBalde(chave: string, granularidade: Granularidade): string {
  switch (granularidade) {
    case 'dia':
      return ddmm(dataUtc(chave));
    case 'semana': {
      const segunda = segundaDaChaveSemanal(chave);
      return `${ddmm(segunda)}–${ddmm(somarDias(segunda, 6))}`;
    }
    case 'mes': {
      const [ano, mes] = chave.split('-');
      const indice = Number(mes ?? 1) - 1;
      return `${MESES_CURTOS[indice] ?? '?'}/${(ano ?? '').slice(2)}`;
    }
  }
}

/** Nome longo de um balde para leitura corrida ("semana de 21/09 a 27/09"). */
export function descreverBalde(balde: Balde, granularidade: Granularidade): string {
  switch (granularidade) {
    case 'dia':
      return `dia ${balde.rotulo}`;
    case 'semana':
      return `semana de ${balde.rotulo.replace('–', ' a ')}`;
    case 'mes':
      return `mês de ${balde.rotulo}`;
  }
}

/** Granularidade que cabe na janela: ate 31 dias por dia, ate 183 por semana, acima por mes. */
export function granularidadePadrao(dias: number | null): Granularidade {
  if (dias === null) {
    return 'semana';
  }
  if (dias <= 31) {
    return 'dia';
  }
  if (dias <= 183) {
    return 'semana';
  }
  return 'mes';
}

export function agregarSerie(points: readonly DailyPoint[], granularidade: Granularidade): SerieAgregada {
  const baldes = new Map<string, Balde>();
  for (const p of points) {
    const chave = chaveDoBalde(p.day, granularidade);
    const atual = baldes.get(chave) ?? {
      chave,
      rotulo: rotuloDoBalde(chave, granularidade),
      abandonadoCents: 0,
      recuperadoCents: 0,
      abandonadoCount: 0,
      recuperadoCount: 0,
    };
    baldes.set(chave, {
      ...atual,
      abandonadoCents: atual.abandonadoCents + p.abandonedAmountCents,
      recuperadoCents: atual.recuperadoCents + p.recoveredAmountCents,
      abandonadoCount: atual.abandonadoCount + p.abandonedCount,
      recuperadoCount: atual.recuperadoCount + p.recoveredCount,
    });
  }
  // As chaves sao ordenaveis como texto dentro da mesma granularidade, entao a
  // ordem cronologica nao depende de a serie ter chegado ordenada.
  const lista = [...baldes.values()].sort((a, b) => (a.chave < b.chave ? -1 : a.chave > b.chave ? 1 : 0));
  return {
    granularidade,
    baldes: lista,
    labels: lista.map((b) => b.rotulo),
    abandonadoCents: lista.map((b) => b.abandonadoCents),
    recuperadoCents: lista.map((b) => b.recuperadoCents),
  };
}
